import React, { useEffect, useState, useMemo } from "react";
import { Modal, Box, Typography, IconButton, CircularProgress, Button } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import CampaignJob from "../../../interfaces/campaignJob";
import CampaignService from "../../../services/campaignJobService";
import CallLogResponse from "../../../interfaces/callLogResponse";
import SearchResultGrid from "../../searchResultGrid/searchResultGrid";
import callLogColumnDef from "../../../colDef/callLogColumnDef";
import useIsMounted from "../../../utils/useMountedRef";
import apiClient from "../../../axiosClient";

interface CallLogsModalProps {
    open: boolean;
    onClose: () => void;
    currentCampaignJob: CampaignJob;
};

const styles = {
    modalStyle: {
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        height: '80%',
        bgcolor: 'background.paper',
        borderRadius: 4,
        boxShadow: 24,
        p: 4,
        overflowY: 'auto'
    },
    modalBox: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    loaderBox: {
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    grid: {
        mt: 2
    },
    centralTextBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '90%',
        mt: 1
    },
    boldText: {
        fontWeight: 'bold'
    },
    aggregationBox: {
        mt: 2,
        mb: 2,
        p: 2,
        backgroundColor: '#f5f5f5',
        borderRadius: 2
    },
    statCard: {
        textAlign: 'center' as 'center',
        p: 2
    },
    statValue: {
        fontSize: '1.5rem',
        fontWeight: 'bold',
        color: 'primary.main'
    },
    statLabel: {
        fontSize: '0.875rem',
        color: 'text.secondary',
        mt: 0.5
    },
    refreshButton: {
        mt: 1,
        mb: 1
    },
    aggregationHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2
    },
    breakdownBox: {
        mt: 2,
        p: 2,
        backgroundColor: '#ffffff',
        borderRadius: 2,
        border: '1px solid #e0e0e0'
    },
    breakdownItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 0.5,
        borderBottom: '1px solid #f5f5f5',
        '&:last-child': {
            borderBottom: 'none'
        }
    },
    breakdownLabel: {
        fontSize: '0.875rem',
        color: 'text.primary'
    },
    breakdownValue: {
        fontSize: '0.875rem',
        fontWeight: 'bold',
        color: 'primary.main'
    }
};

const CallLogsModal: React.FC<CallLogsModalProps> = ({ open, onClose, currentCampaignJob }) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isRefreshingAggregations, setIsRefreshingAggregations] = useState<boolean>(false);
    const [aggregatesError, setAggregatesError] = useState<boolean>(false);
    const [callLogs, setCallLogs] = useState<CallLogResponse[]>([]);
    const [totalRowCount, setTotalRowCount] = useState<number>(0);
    const [apiAggregations, setApiAggregations] = useState<any>(null);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
    const isMountedRef = useIsMounted();
    
    const aggregations = useMemo(() => {
        if (!apiAggregations?.aggregates) {
            return {
                totalAttempts: 'N/A',
                connectedCalls: 'N/A',
                connectionRate: 'N/A',
                totalDuration: 'N/A',
                avgDuration: 'N/A',
                byResult: {}
            };
        }

        const agg = apiAggregations.aggregates;
        const totalAttempts = agg.total_count || 0;
        const connectedCalls = (agg.by_result?.Answered || 0);
        const connectionRate = totalAttempts > 0 ? (connectedCalls / totalAttempts) * 100 : 0;
        const totalDuration = agg.total_duration_seconds || 0;
        const avgDuration = connectedCalls > 0 ? totalDuration / connectedCalls : 0;

        return {
            totalAttempts,
            connectedCalls,
            connectionRate,
            totalDuration,
            avgDuration,
            byResult: agg.by_result || {}
        };
    }, [apiAggregations]);

    const formatDuration = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        
        if (hours > 0) {
            return `${hours}h ${minutes}m ${secs}s`;
        } else if (minutes > 0) {
            return `${minutes}m ${secs}s`;
        }
        return `${secs}s`;
    };

    const fetchCallLogs = async () => {
        try {
            setIsLoading(true);
            // Use page_index directly from paginationModel.page
            const response = await CampaignService.getCampaignJobResults(
                currentCampaignJob.campaign_type, 
                currentCampaignJob.campaign_job_id,
                paginationModel.page
            );
            if (!isMountedRef.current) return;
            // Handle response structure: 
            // Call campaign: { call_logs: CallLogResponse[], rows_count: number }
            // Knowl campaign: CallLogResponse[] (direct array, no wrapper)
            const rawLogs = response?.call_logs || response || [];
            const logsArray = Array.isArray(rawLogs) ? rawLogs : [];
            const rowCount = response?.rows_count || logsArray.length || 0;
            setCallLogs(logsArray);
            setTotalRowCount(rowCount);
        } catch (_) {} finally {
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        }
    };

    const refreshAggregations = async () => {
        try {
            setIsRefreshingAggregations(true);
            setAggregatesError(false);
            const response = await CampaignService.getCallCampaignAggregations(currentCampaignJob.campaign_job_id);
            if (!isMountedRef.current) return;
            setApiAggregations(response);
        } catch (error) {
            console.error('Failed to fetch aggregations:', error);
            if (isMountedRef.current) {
                setAggregatesError(true);
            }
        } finally {
            if (isMountedRef.current) {
                setIsRefreshingAggregations(false);
            }
        }
    };

    const [isDownloadingFullFile, setIsDownloadingFullFile] = useState<boolean>(false);

    const downloadFullCSV = async () => {
        try {
            setIsDownloadingFullFile(true);
            const response = await apiClient.get(
                `/api/v1/campaigns/call-campaign/${currentCampaignJob.campaign_job_id}/download-file`,
                { responseType: 'blob' }
            );
            
            // Create a blob URL and trigger download
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `call_logs_${currentCampaignJob.campaign_job_id}_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download CSV:', error);
            alert('Failed to download file. Please try again.');
        } finally {
            setIsDownloadingFullFile(false);
        }
    };

    useEffect(() => {
        fetchCallLogs();
    }, [paginationModel]);

    useEffect(() => {
        refreshAggregations(); // Fetch aggregates once on mount
    }, []);

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={styles.modalStyle}>
                <Box sx={styles.modalBox}>
                    <Typography id="modal-title" variant="body1">
                        Call Logs for Campaign Name:
                        <Typography sx={styles.boldText} component="span"> {currentCampaignJob.campaign_name} </Typography>
                        & Job ID:
                        <Typography sx={styles.boldText} component="span"> {currentCampaignJob.campaign_job_id}</Typography>
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                {isLoading ? (
                    <Box sx={styles.loaderBox}>
                        <CircularProgress color="primary" />
                    </Box>
                ) : Array.isArray(callLogs) && callLogs.length > 0 ? (
                    <>
                        <Box sx={styles.aggregationBox}>
                            <Box sx={styles.aggregationHeader}>
                                <Typography variant="h6">Campaign Statistics</Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={isRefreshingAggregations ? <CircularProgress size={16} /> : <RefreshIcon />}
                                    onClick={refreshAggregations}
                                    disabled={isRefreshingAggregations}
                                >
                                    {isRefreshingAggregations ? 'Refreshing...' : 'Refresh Stats'}
                                </Button>
                            </Box>
                            
                            {aggregatesError ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                                    <Typography color="error" sx={{ mb: 2 }}>
                                        Failed to load statistics. Please try again.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<RefreshIcon />}
                                        onClick={refreshAggregations}
                                    >
                                        Retry
                                    </Button>
                                </Box>
                            ) : isRefreshingAggregations && !apiAggregations ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                                    <CircularProgress size={32} />
                                    <Typography sx={{ ml: 2 }}>Loading statistics...</Typography>
                                </Box>
                            ) : (
                                <>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'space-around' }}>
                                        <Box sx={styles.statCard}>
                                            <Typography sx={styles.statValue}>
                                                {aggregations.totalAttempts}
                                            </Typography>
                                            <Typography sx={styles.statLabel}>
                                                Total Call Attempts
                                            </Typography>
                                        </Box>
                                        <Box sx={styles.statCard}>
                                            <Typography sx={styles.statValue}>
                                                {typeof aggregations.connectionRate === 'number' 
                                                    ? `${aggregations.connectionRate.toFixed(1)}%` 
                                                    : aggregations.connectionRate}
                                            </Typography>
                                            <Typography sx={styles.statLabel}>
                                                Connection Rate
                                            </Typography>
                                        </Box>
                                        <Box sx={styles.statCard}>
                                            <Typography sx={styles.statValue}>
                                                {typeof aggregations.totalDuration === 'number'
                                                    ? formatDuration(aggregations.totalDuration)
                                                    : aggregations.totalDuration}
                                            </Typography>
                                            <Typography sx={styles.statLabel}>
                                                Total Talktime
                                            </Typography>
                                        </Box>
                                        <Box sx={styles.statCard}>
                                            <Typography sx={styles.statValue}>
                                                {typeof aggregations.avgDuration === 'number'
                                                    ? formatDuration(aggregations.avgDuration)
                                                    : aggregations.avgDuration}
                                            </Typography>
                                            <Typography sx={styles.statLabel}>
                                                Avg Call Duration
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {Object.keys(aggregations.byResult).length > 0 && (
                                        <Box sx={styles.breakdownBox}>
                                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                                Call Results Breakdown
                                            </Typography>
                                            {Object.entries(aggregations.byResult)
                                                .sort(([, a], [, b]) => (b as number) - (a as number))
                                                .map(([result, count]) => {
                                                    const countNum = count as number;
                                                    const percentage = ((countNum / (aggregations.totalAttempts as number)) * 100).toFixed(1);
                                                    return (
                                                        <Box key={result} sx={styles.breakdownItem}>
                                                            <Typography sx={styles.breakdownLabel}>
                                                                {result}
                                                            </Typography>
                                                            <Typography sx={styles.breakdownValue}>
                                                                {countNum} ({percentage}%)
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })}
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>
                        <Box sx={styles.grid}>
                            <SearchResultGrid
                                results={callLogs}
                                columns={callLogColumnDef}
                                getRowId={(val: CallLogResponse) => val.call_log_id}
                                rowCount={totalRowCount}
                                paginationModel={paginationModel}
                                setPaginationModel={setPaginationModel}
                                onSelectRow={() => {}}
                                height="55vh"
                                rowHeight={60}
                                isRowSelectionAllowed={false}
                                disableColumnSelector
                                onDownloadFullFile={downloadFullCSV}
                                downloadFullFileButtonText="Download Full File"
                                isDownloadingFullFile={isDownloadingFullFile}
                            />
                        </Box>
                    </>
                ) : (
                    <Box sx={styles.centralTextBox}>
                        <Typography>
                            No Call Logs for this job
                        </Typography>
                    </Box>
                )}
            </Box>
        </Modal>
    );
};

export default CallLogsModal;
